import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import {
  FiCheckCircle,
  FiExternalLink,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMinimize2,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import InteractiveLesson from './InteractiveLesson';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, IVORY_GRADIENT_TEXT, Page, Kicker, Card, Tag, EmptyState, Button, buttonStyle } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/classroom.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };

const CONFETTI_COLORS = [BRAND.gold, BRAND.goldLight, BRAND.goldPale, BRAND.ivory, BRAND.success];

function lessonIdFromProgressEntry(lp) {
  if (!lp?.lesson) return null;
  return typeof lp.lesson === 'object' ? lp.lesson._id : lp.lesson;
}

function toEmbedVideoUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const u = new URL(trimmed);
    if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    }
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      return id ? `https://www.youtube.com/embed/${id}` : trimmed;
    }
  } catch {
    return trimmed;
  }
  return trimmed;
}

function ConfettiBurst({ active }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 280,
        y: -20 - Math.random() * 120,
        rot: (Math.random() - 0.5) * 720,
        delay: Math.random() * 0.25,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        w: 8 + Math.random() * 10,
        h: 6 + Math.random() * 8,
      })),
    []
  );

  if (!active) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9998, overflow: 'hidden' }}>
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ opacity: 1, x: '50vw', y: '42vh', rotate: 0, scale: 1 }}
          animate={{ opacity: [1, 1, 0], x: `calc(50vw + ${p.x}px)`, y: `calc(42vh + ${p.y + 320}px)`, rotate: p.rot, scale: [1, 1.2, 0.6] }}
          transition={{ duration: 2.4, delay: p.delay, ease: 'easeOut' }}
          style={{ position: 'absolute', width: p.w, height: p.h, borderRadius: 3, background: p.color, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}
        />
      ))}
    </div>
  );
}

const navBtn = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 46, height: 46, borderRadius: '50%',
  background: 'rgba(7,20,45,0.6)', backdropFilter: 'blur(8px)', border: `1px solid ${BRAND.line}`, color: BRAND.ivory,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s',
};

function SlideViewer({ slides, title }) {
  const [current, setCurrent] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const total = slides.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); setCurrent((c) => Math.min(c + 1, total - 1)); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setCurrent((c) => Math.max(c - 1, 0)); }
      if (e.key === 'Escape' && fullscreen) setFullscreen(false);
      if (e.key === 'f' || e.key === 'F') setFullscreen((f) => !f);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total, fullscreen]);

  const prev = () => setCurrent((c) => Math.max(c - 1, 0));
  const next = () => setCurrent((c) => Math.min(c + 1, total - 1));
  const pct = total > 1 ? ((current + 1) / total) * 100 : 100;
  const slide = slides[current];

  const containerStyle = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 9999, background: BRAND.navy900, display: 'flex', flexDirection: 'column' }
    : { borderRadius: 22, overflow: 'hidden', border: `1px solid ${BRAND.line}`, boxShadow: '0 18px 50px rgba(0,0,0,0.4)', background: BRAND.navy800 };

  const hoverIn = (e) => { e.currentTarget.style.background = 'rgba(205,155,80,0.35)'; };
  const hoverOut = (e) => { e.currentTarget.style.background = 'rgba(7,20,45,0.6)'; };

  return (
    <div style={containerStyle}>
      <div style={{ position: 'relative', flex: fullscreen ? 1 : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: fullscreen ? 0 : 'min(60vw, 520px)', background: BRAND.navy900 }}>
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={slide.image}
            alt={slide.caption || `Slide ${current + 1}`}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            style={{ maxWidth: '100%', maxHeight: fullscreen ? '85vh' : 'min(60vw, 520px)', objectFit: 'contain', display: 'block', margin: '0 auto' }}
            draggable={false}
          />
        </AnimatePresence>

        {current > 0 && (
          <button type="button" onClick={prev} aria-label="Previous slide" style={{ ...navBtn, left: 12 }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            <FiChevronLeft size={24} />
          </button>
        )}
        {current < total - 1 && (
          <button type="button" onClick={next} aria-label="Next slide" style={{ ...navBtn, right: 12 }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            <FiChevronRight size={24} />
          </button>
        )}

        <button
          type="button" onClick={() => setFullscreen((f) => !f)} aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          style={{ position: 'absolute', top: 12, right: 12, width: 38, height: 38, borderRadius: 12, background: 'rgba(7,20,45,0.6)', backdropFilter: 'blur(8px)', border: `1px solid ${BRAND.line}`, color: BRAND.ivory, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {fullscreen ? <FiMinimize2 size={17} /> : <FiMaximize2 size={17} />}
        </button>
      </div>

      <div style={{ padding: '12px 18px', background: BRAND.navy800, borderTop: `1px solid ${BRAND.lineSoft}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <span style={{ fontWeight: 800, fontSize: '0.86rem', color: BRAND.goldLight }}>{current + 1} / {total}</span>
          {slide.caption && <span style={{ fontWeight: 600, fontSize: '0.85rem', color: BRAND.muted, textAlign: 'right', flex: 1 }}>{slide.caption}</span>}
          <span style={{ fontWeight: 600, fontSize: '0.76rem', color: BRAND.faint }}>Use arrows or click</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(226,184,120,0.14)', overflow: 'hidden' }}>
          <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} style={{ height: '100%', borderRadius: 2, background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight})` }} />
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          {slides.map((_, i) => (
            <button
              key={i} type="button" onClick={() => setCurrent(i)} aria-label={`Go to slide ${i + 1}`}
              style={{ width: i === current ? 24 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === current ? BRAND.goldLight : i < current ? BRAND.gold : 'rgba(249,240,231,0.15)', transition: 'all 0.25s' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const backLink = { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 800, color: BRAND.goldLight, fontSize: '0.9rem' };

export default function LessonDetail() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  const backPath = isAdmin ? '/admin/corsi' : '/lezioni';
  const backLabel = isAdmin ? 'Back to Courses' : 'Back to Lessons';

  const alreadyCompleted = useMemo(() => {
    if (isAdmin || !lesson?._id || !progress?.lessons) return false;
    const lid = String(lesson._id);
    return progress.lessons.some((lp) => {
      const pid = lessonIdFromProgressEntry(lp);
      return pid && String(pid) === lid && lp.completed;
    });
  }, [lesson, progress, isAdmin]);

  const showSuccess = alreadyCompleted || justCompleted;

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const requests = [api.get(`/lessons/${id}`)];
      if (!isAdmin) requests.push(api.get('/progress/my').catch(() => ({ data: { progress: null } })));
      const results = await Promise.all(requests);
      setLesson(results[0].data?.lesson || null);
      if (!isAdmin) setProgress(results[1]?.data?.progress || null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Unable to load the lesson.');
      setLesson(null);
    } finally {
      setLoading(false);
    }
  }, [id, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleComplete = useCallback(async () => {
    if (!id || completing || alreadyCompleted || justCompleted) return;
    setCompleting(true);
    try {
      const { data: completeRes } = await api.post(`/lessons/${id}/complete`, {});
      setJustCompleted(true);
      if (!completeRes?.alreadyCompleted) {
        setCelebrate(true);
        toast.success('Great! Lesson complete! +10 points 🎉');
        window.setTimeout(() => setCelebrate(false), 2800);
      }
      const { data } = await api.get('/progress/my');
      setProgress(data?.progress || null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Something went wrong. Try again!');
    } finally {
      setCompleting(false);
    }
  }, [id, completing, alreadyCompleted, justCompleted]);

  const handleAward = useCallback(async (award) => {
    if (!id || isAdmin || !award?.id) return;
    try {
      const { data } = await api.post('/progress/badges', { id: award.id, name: award.name, icon: award.icon, desc: award.desc, lesson: id });
      setProgress((p) => (p ? { ...p, badges: data?.badges || p.badges, totalPoints: data?.totalPoints ?? p.totalPoints } : p));
    } catch {
      /* silently ignore: the badge is still shown locally */
    }
  }, [id, isAdmin]);

  const openFamilyPage = useCallback(() => {
    const courseId = typeof lesson?.course === 'object' ? lesson?.course?._id : lesson?.course;
    const q = new URLSearchParams();
    if (courseId) q.set('course', courseId);
    if (id) q.set('lesson', id);
    window.open(`${isAdmin ? '/admin/famiglia' : '/famiglia'}?${q.toString()}`, '_blank', 'noopener');
  }, [lesson, id, isAdmin]);

  const isInteractive = lesson?.lessonType === 'interactive' && Array.isArray(lesson?.interactiveSlides) && lesson?.interactiveSlides.length > 0;

  const sanitizedHtml = lesson?.content ? DOMPurify.sanitize(lesson.content, { USE_PROFILES: { html: true } }) : '';
  const embedUrl = lesson?.videoUrl ? toEmbedVideoUrl(lesson.videoUrl) : '';

  if (loading) {
    return <Page hero={HERO}><LoadingSpinner message="Preparing the lesson…" /></Page>;
  }

  if (!lesson) {
    return (
      <Page hero={HERO}>
        <EmptyState icon="🔎" title="Lesson not found" text="This lesson doesn't exist or isn't available for your course." action={<Button to={backPath} variant="outline"><FiArrowLeft /> {backLabel}</Button>} />
      </Page>
    );
  }

  if (isInteractive) {
    // The interactive player draws its own full-width CORE scene; keep the wrapper minimal.
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: BRAND.navy900, padding: '12px 0 32px', position: 'relative', zIndex: 1, fontFamily: BRAND.font }}>
        <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} style={{ marginBottom: 14, padding: '0 clamp(14px,2.5vw,32px)' }}>
          <Link to={backPath} style={backLink}><FiArrowLeft size={17} /> {backLabel}</Link>
        </motion.div>
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <InteractiveLesson slides={lesson.interactiveSlides} title={lesson.title} description={lesson.description} onBack={() => navigate(backPath)} backLabel={backLabel} onComplete={isAdmin ? undefined : handleComplete}
            onAward={isAdmin ? undefined : handleAward} onFamilyPrint={openFamilyPage}
            earnedBadges={(progress?.badges || []).filter((b) => !b.lesson || String(typeof b.lesson === 'object' ? b.lesson._id : b.lesson) === String(id))} />
        </motion.section>
      </div>
    );
  }

  const done = alreadyCompleted || justCompleted;

  return (
    <Page hero={HERO} maxWidth={1000}>
      <ConfettiBurst active={celebrate} />

      <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Link to={backPath} style={backLink}><FiArrowLeft size={18} /> {backLabel}</Link>
        {(user?.displayName || user?.name) && (
          <span style={{ fontFamily: BRAND.serif, fontStyle: 'italic', color: BRAND.muted, fontSize: '1.05rem' }}>Hi, {user.displayName || user.name}! Ready to learn?</span>
        )}
      </motion.div>

      <AnimatePresence>
        {showSuccess && (
          <motion.div initial={{ opacity: 0, scale: 0.96, y: -8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            style={{ marginBottom: 18, padding: '14px 18px', borderRadius: 18, background: 'rgba(95,191,138,0.12)', border: '1px solid rgba(95,191,138,0.45)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <FiCheckCircle size={24} color="#8fd9ad" />
            <div>
              <div style={{ fontWeight: 900, fontSize: '1rem' }}>Lesson complete!</div>
              <div style={{ fontSize: '0.88rem', color: BRAND.muted }}>{justCompleted ? 'Awesome! Keep it up!' : "You've already completed this lesson. Review anytime you like!"}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        style={{ marginBottom: 22, padding: 'clamp(22px,3vw,34px)', borderRadius: 24, position: 'relative', overflow: 'hidden', border: `1px solid ${BRAND.line}`, background: BRAND.navy800, boxShadow: '0 18px 50px rgba(0,0,0,0.4)' }}>
        <img src={BRAND.bg.classroom} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25 }} />
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(11,26,58,0.97) 0%, rgba(11,26,58,0.8) 60%, rgba(11,26,58,0.5) 100%)' }} />
        <div style={{ position: 'relative' }}>
          <Kicker>Lesson</Kicker>
          <h1 style={{ fontSize: 'clamp(1.6rem,3.4vw,2.3rem)', fontWeight: 900, lineHeight: 1.1, margin: '8px 0 10px', ...IVORY_GRADIENT_TEXT }}>{lesson.title}</h1>
          {lesson.description && <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.2rem', lineHeight: 1.35, color: 'rgba(249,240,231,0.88)', margin: 0, maxWidth: 680 }}>{lesson.description}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {lesson.lessonType && <Tag tone="muted">{lesson.lessonType === 'slides' ? 'Slides' : 'Reading'}</Tag>}
            {done && <Tag tone="success"><FiCheckCircle size={12} /> Done</Tag>}
          </div>
        </div>
      </motion.header>

      {lesson.lessonType === 'slides' && Array.isArray(lesson.slides) && lesson.slides.length > 0 ? (
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.45 }} style={{ marginBottom: 22 }}>
          <SlideViewer slides={lesson.slides} title={lesson.title} />
        </motion.section>
      ) : (
        <>
          <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.45 }}
            style={{ marginBottom: 22, borderRadius: 22, overflow: 'hidden', border: `1px solid ${BRAND.line}`, boxShadow: '0 18px 50px rgba(0,0,0,0.4)', background: BRAND.navy800 }}>
            {embedUrl ? (
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, background: BRAND.navy900 }}>
                <iframe title={`Video: ${lesson.title}`} src={embedUrl} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
              </div>
            ) : (
              <div style={{ minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 28, textAlign: 'center' }}>
                <img src={BRAND.owl} alt="" style={{ width: 54, opacity: 0.8 }} />
                <p style={{ fontWeight: 900, fontSize: '1.05rem', margin: 0 }}>No video for this lesson</p>
                <p style={{ color: BRAND.muted, maxWidth: 380, margin: 0, fontSize: '0.92rem' }}>Read the text below — it's full of interesting stuff!</p>
              </div>
            )}
          </motion.section>

          <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, duration: 0.45 }} style={{ marginBottom: 22 }}>
            <Card style={{ padding: 'clamp(20px,3vw,30px)', background: BRAND.ivory, color: BRAND.ink, border: `1px solid ${BRAND.lineIvory}` }}>
              <Kicker light style={{ marginBottom: 12 }}>Lesson content</Kicker>
              <div className="lesson-html-content" style={{ fontSize: '1.02rem', lineHeight: 1.65, color: BRAND.ink }} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            </Card>
          </motion.article>
        </>
      )}

      {Array.isArray(lesson.resources) && lesson.resources.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.45 }} style={{ marginBottom: 22 }}>
          <Card>
            <Kicker style={{ marginBottom: 12 }}>Extra resources</Kicker>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lesson.resources.map((res, idx) => (
                <li key={res.url || res.title || idx}>
                  <a href={/^https?:\/\//i.test(res.url || '') ? res.url : '#'} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 14, background: 'rgba(249,240,231,0.05)', color: BRAND.ivory, fontWeight: 700, fontSize: '0.94rem', border: `1px solid ${BRAND.lineSoft}` }}>
                    <FiExternalLink size={18} color={BRAND.goldLight} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {res.title || 'Resource'}
                      {res.type && <span style={{ fontWeight: 600, color: BRAND.muted, marginLeft: 8, fontSize: '0.78rem' }}>({res.type})</span>}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </motion.section>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.45 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 14, padding: 22 }}>
          <button
            type="button" onClick={handleComplete} disabled={completing || done}
            style={{
              ...buttonStyle(done ? 'outline' : 'gold', 'lg'), width: '100%', fontSize: '1.05rem',
              background: done ? 'rgba(95,191,138,0.12)' : GOLD_GRADIENT, borderColor: done ? 'rgba(95,191,138,0.5)' : 'transparent', color: done ? '#8fd9ad' : BRAND.navy900,
              opacity: completing ? 0.75 : 1, cursor: completing ? 'wait' : done ? 'default' : 'pointer', boxShadow: done ? 'none' : undefined,
            }}
          >
            {completing ? 'Saving…' : justCompleted ? (<><FiCheckCircle size={22} /> Completed! 🎉</>) : alreadyCompleted ? (<><FiCheckCircle size={22} /> Already completed</>) : (<><FiCheckCircle size={22} /> Mark lesson complete</>)}
          </button>
          <Link to={backPath} style={{ ...backLink, justifyContent: 'center' }}>← {backLabel}</Link>
        </Card>
      </motion.div>
    </Page>
  );
}
